import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { Button, Stack, Text, Title } from "@mantine/core";
import { lazy, useCallback, useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { AccessPending } from "../features/auth/AccessPending.js";
import { AdministratorLogin } from "../features/auth/AdministratorLogin.js";
import { AuthPageFrame } from "../features/auth/AuthPageFrame.js";
import { LoginPage } from "../features/auth/LoginPage.js";
import {
  AdministrationFrame,
  AdministrationLanding,
} from "../features/admin/AdminNavigation.js";
import {
  PersistentFilterWorkspace,
} from "../features/filters/FilterWorkspace.js";
import { FilterStateProvider } from "../features/filters/FilterStateProvider.js";
import { SavedViewsPage } from "../features/filters/SavedViewsPage.js";
import { clearVideoPlaybackUrlCache } from "../features/video/api.js";
import { CustomerShell } from "../features/workspace/CustomerShell.js";
import {
  getAccess,
  getSession,
  PROTECTED_QUERY_KEY,
  SessionExpiredError,
} from "../shared/api/client.js";
import {
  AuthenticatedAnalyticsProvider,
  clearAnalyticsSession,
} from "../shared/analytics/AnalyticsProvider.js";
import { ScreenTracker } from "../shared/analytics/ScreenTracker.js";
import {
  consumeCustomerReturnTarget,
  storeCustomerReturnTarget,
  storeCustomerReturnTargetIfMissing,
  type ReturnTargetStorage,
} from "../shared/auth/customerReturnTarget.js";

const authQueryKey = ["auth"] as const;
const sessionQueryKey = [...authQueryKey, "session"] as const;
const accessQueryKey = [...authQueryKey, "access"] as const;
const SESSION_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1_000;

const BrandsAdminPage = lazy(async () => ({
  default: (await import("../features/admin/brands/BrandsAdminPage.js")).BrandsAdminPage,
}));
const DomainsAdminPage = lazy(async () => ({
  default: (await import("../features/admin/domains/DomainsAdminPage.js")).DomainsAdminPage,
}));
const SubgroupsAdminPage = lazy(async () => ({
  default: (await import("../features/admin/subgroups/SubgroupsAdminPage.js")).SubgroupsAdminPage,
}));
const PeopleAdminPage = lazy(async () => ({
  default: (await import("../features/admin/users/PeopleAdminPage.js")).PeopleAdminPage,
}));
const StrategistReportPage = lazy(async () => ({
  default: (await import("../features/strategists/StrategistReportPage.js")).StrategistReportPage,
}));

const EditorReportPage = lazy(async () => ({
  default: (await import("../features/strategists/EditorReportPage.js")).EditorReportPage,
}));

/**
 * The creative strategist report (`/strategists/2026-10`) and the video editor
 * report (`/editors/2026-10`) are open to every signed-in user, with or without
 * brand access. They are not in the navigation: people reach them by link.
 */
export const OPEN_REPORT_PATH = "/strategists";
export const OPEN_EDITOR_REPORT_PATH = "/editors";

function isOpenReportPath(pathname: string): boolean {
  return [OPEN_REPORT_PATH, OPEN_EDITOR_REPORT_PATH].some((path) =>
    pathname === path || pathname.startsWith(`${path}/`));
}

function isOpenReportTarget(target: string | null): boolean {
  return target !== null && isOpenReportPath(target.split(/[?#]/u)[0] ?? "");
}

export function clearProtectedQueries(queryClient: QueryClient): void {
  void queryClient.cancelQueries({ queryKey: PROTECTED_QUERY_KEY });
  // Removal is synchronous so a replacement account cannot mount between
  // cancellation and a promise continuation and observe the old cache.
  queryClient.removeQueries({ queryKey: PROTECTED_QUERY_KEY });
  clearVideoPlaybackUrlCache();
}

function AuthenticatedShell({
  hasReportAccess,
  onAccessChanged,
  onSessionExpired,
  role,
}: {
  readonly hasReportAccess: boolean;
  readonly onAccessChanged: () => void;
  readonly onSessionExpired: () => void;
  readonly role: "admin" | "super_admin" | "viewer";
}) {
  const adminRole = role === "viewer" ? null : role;
  return (
    <>
      <ScreenTracker />
      <Routes>
      <Route element={<CustomerShell hasReportAccess={hasReportAccess} role={role} />}>
        {hasReportAccess ? (
          <Route
            element={(
              <FilterStateProvider onSessionExpired={onSessionExpired}>
                <Outlet />
              </FilterStateProvider>
            )}
          >
            <Route
              element={(
                <PersistentFilterWorkspace
                  onAccessChanged={onAccessChanged}
                  onSessionExpired={onSessionExpired}
                />
              )}
              index
            />
            <Route
              element={<SavedViewsPage onSessionExpired={onSessionExpired} />}
              path="saved-views"
            />
          </Route>
        ) : (
          <Route
            element={(
              <section aria-labelledby="report-access-unavailable" className="customer-route">
                <Title id="report-access-unavailable" order={2}>Administration available</Title>
                <Text c="dimmed">
                  No report scope is currently assigned. Customer report data has not been loaded.
                </Text>
              </section>
            )}
            index
          />
        )}
        <Route
          element={<StrategistReportPage onSessionExpired={onSessionExpired} />}
          path={`${OPEN_REPORT_PATH.slice(1)}/:month?`}
        />
        <Route
          element={<EditorReportPage onSessionExpired={onSessionExpired} />}
          path={`${OPEN_EDITOR_REPORT_PATH.slice(1)}/:month?`}
        />
        <Route
          element={adminRole ? (
            <AdministrationFrame role={adminRole}>
              <AdministrationLanding />
            </AdministrationFrame>
          ) : <Navigate replace to="/" />}
          path="admin"
        />
        <Route
          element={adminRole ? (
            <AdministrationFrame role={adminRole}>
              <PeopleAdminPage onSessionExpired={onSessionExpired} role={adminRole} />
            </AdministrationFrame>
          ) : <Navigate replace to="/" />}
          path="admin/users"
        />
        <Route
          element={adminRole ? (
            <AdministrationFrame role={adminRole}>
              <SubgroupsAdminPage onSessionExpired={onSessionExpired} />
            </AdministrationFrame>
          ) : <Navigate replace to="/" />}
          path="admin/subgroups"
        />
        <Route
          element={role === "super_admin" ? (
            <AdministrationFrame role={role}>
              <BrandsAdminPage onSessionExpired={onSessionExpired} />
            </AdministrationFrame>
          ) : <Navigate replace to="/admin" />}
          path="admin/brands"
        />
        <Route
          element={role === "super_admin" ? (
            <AdministrationFrame role={role}>
              <DomainsAdminPage onSessionExpired={onSessionExpired} />
            </AdministrationFrame>
          ) : <Navigate replace to="/admin" />}
          path="admin/domains"
        />
        <Route element={<Navigate replace to="/" />} path="*" />
      </Route>
      </Routes>
    </>
  );
}

function LoadingShell() {
  return (
    <main aria-busy="true" aria-live="polite" className="app-shell">
      <p role="status">Checking your sign-in…</p>
    </main>
  );
}

function AuthUnavailable({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <AuthPageFrame>
      <Stack gap="md">
        <Title order={1}>Sign-in check unavailable</Title>
        <Text c="dimmed">
          We could not verify your current sign-in. Your existing session has not
          been cleared.
        </Text>
        <Button onClick={onRetry} variant="light">Retry sign-in check</Button>
      </Stack>
    </AuthPageFrame>
  );
}

function browserSessionStorage(): ReturnTargetStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function CustomerLoginRedirect({ sessionExpired }: { readonly sessionExpired: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const destination = sessionExpired ? "/login?session_expired=1" : "/login";
  useEffect(() => {
    const storage = browserSessionStorage();
    const target = `${location.pathname}${location.search}${location.hash}`;
    if (sessionExpired && target === "/") {
      storeCustomerReturnTargetIfMissing(storage, target, window.location.origin);
    } else {
      storeCustomerReturnTarget(storage, target, window.location.origin);
    }
    navigate(destination, { replace: true });
  }, [destination, location.hash, location.pathname, location.search, navigate]);
  return <LoadingShell />;
}

function SessionGate() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const recoveryStarted = useRef(false);
  const [customerRecovery, setCustomerRecovery] = useState<{
    readonly checked: boolean;
    readonly target: string | null;
  }>({ checked: false, target: null });
  const [expired, setExpired] = useState(false);
  const authorizationRefreshActive = useRef(false);
  const automaticallyRecheckedPermissionVersion = useRef<string | null>(null);
  const observedPermissionVersion = useRef<string | null>(null);
  const [authorizationRefreshing, setAuthorizationRefreshing] = useState(false);
  const session = useQuery({
    queryKey: sessionQueryKey,
    queryFn: ({ signal }) => getSession({ signal }),
    refetchInterval: (query) => query.state.data?.authenticated === true
      ? SESSION_REFRESH_INTERVAL_MS
      : false,
    refetchIntervalInBackground: true,
  });
  const access = useQuery({
    enabled: session.data?.authenticated === true,
    queryKey: accessQueryKey,
    queryFn: ({ signal }) => getAccess({ signal }),
  });

  const expireSession = useCallback(() => {
    clearAnalyticsSession();
    setExpired(true);
    queryClient.setQueryData(sessionQueryKey, { authenticated: false });
    queryClient.removeQueries({ queryKey: accessQueryKey });
    clearProtectedQueries(queryClient);
  }, [queryClient]);

  const runAuthorizationRefresh = useCallback(() => {
    if (authorizationRefreshActive.current) return;
    authorizationRefreshActive.current = true;
    clearProtectedQueries(queryClient);
    setAuthorizationRefreshing(true);
    void queryClient.refetchQueries({
      queryKey: authQueryKey,
      type: "active",
    }).then(() => {
      const refreshedSession = queryClient.getQueryData<
        Awaited<ReturnType<typeof getSession>>
      >(sessionQueryKey);
      if (refreshedSession?.authenticated === true) {
        observedPermissionVersion.current = refreshedSession.permissionVersion;
        automaticallyRecheckedPermissionVersion.current = refreshedSession.permissionVersion;
      }
    }).finally(() => {
      authorizationRefreshActive.current = false;
      setAuthorizationRefreshing(false);
    });
  }, [queryClient]);

  const permissionVersion = session.data?.authenticated === true
    ? session.data.permissionVersion
    : null;
  const refreshAuthorization = useCallback(() => {
    if (authorizationRefreshActive.current) return;
    if (
      permissionVersion !== null
      && automaticallyRecheckedPermissionVersion.current === permissionVersion
    ) {
      // The automatic recheck is bounded to one attempt per permission version
      // so a persistent denial cannot loop. Leave the failed query holding its
      // error rather than replacing the application: the surface that hit the
      // denial renders its own state, the shell stays usable, and changing a
      // filter or brand recovers without a reload. Clearing again here would
      // evict the query, trigger an immediate refetch, and loop.
      return;
    }
    automaticallyRecheckedPermissionVersion.current = permissionVersion;
    runAuthorizationRefresh();
  }, [permissionVersion, queryClient, runAuthorizationRefresh]);

  useEffect(() => {
    if (permissionVersion === null) {
      observedPermissionVersion.current = null;
      return;
    }
    const previousPermissionVersion = observedPermissionVersion.current;
    observedPermissionVersion.current = permissionVersion;
    if (
      previousPermissionVersion === null
      || previousPermissionVersion === permissionVersion
    ) return;
    automaticallyRecheckedPermissionVersion.current = permissionVersion;
    runAuthorizationRefresh();
  }, [permissionVersion, runAuthorizationRefresh]);

  useEffect(() => {
    if (!(access.error instanceof SessionExpiredError)) return;
    expireSession();
  }, [access.error, expireSession]);

  useEffect(() => {
    if (session.data?.authenticated === false) {
      clearAnalyticsSession();
      automaticallyRecheckedPermissionVersion.current = null;
      clearProtectedQueries(queryClient);
    }
  }, [queryClient, session.data?.authenticated]);

  useEffect(() => {
    if (
      session.data?.authenticated !== true
      || !access.isSuccess
      || recoveryStarted.current
    ) return;
    recoveryStarted.current = true;
    const storedTarget = consumeCustomerReturnTarget(
      browserSessionStorage(),
      window.location.origin,
    );
    setCustomerRecovery({
      checked: true,
      target: (access.data.hasAccess || isOpenReportTarget(storedTarget))
        && (location.pathname === "/" || location.pathname === "/login")
        ? storedTarget
        : null,
    });
  }, [access.data, access.isSuccess, location.pathname, session.data?.authenticated]);

  useEffect(() => {
    if (!customerRecovery.checked || customerRecovery.target === null) return;
    const current = `${location.pathname}${location.search}${location.hash}`;
    if (current === customerRecovery.target) {
      setCustomerRecovery({ checked: true, target: null });
    }
  }, [
    customerRecovery.checked,
    customerRecovery.target,
    location.hash,
    location.pathname,
    location.search,
  ]);

  const retry = () => {
    void queryClient.invalidateQueries({ queryKey: authQueryKey });
  };

  if (session.isPending || (session.data?.authenticated && access.isPending)) {
    return <LoadingShell />;
  }
  if (authorizationRefreshing) return <LoadingShell />;
  if (expired || session.error instanceof SessionExpiredError || access.error instanceof SessionExpiredError) {
    if (location.pathname !== "/login") {
      return <CustomerLoginRedirect sessionExpired />;
    }
    return <LoginPage message="Your session has expired. Please sign in again." />;
  }
  if (session.error || access.error) {
    return <AuthUnavailable onRetry={retry} />;
  }
  if (!session.data?.authenticated) {
    if (location.pathname !== "/login") return <CustomerLoginRedirect sessionExpired={false} />;
    const expiredMessage = new URLSearchParams(location.search).has("session_expired")
      ? "Your session has expired. Please sign in again."
      : undefined;
    return <LoginPage message={expiredMessage} />;
  }

  if (!customerRecovery.checked) return <LoadingShell />;
  if (customerRecovery.target !== null) {
    const target = customerRecovery.target;
    const current = `${location.pathname}${location.search}${location.hash}`;
    if (target !== current) return <Navigate replace to={target} />;
  }

  if (location.pathname === "/login") return <Navigate replace to="/" />;

  if (
    session.data.role === "viewer" &&
    (location.pathname === "/admin" || location.pathname.startsWith("/admin/"))
  ) {
    return <Navigate replace to="/" />;
  }

  // This role branch selects the administration-capable shell only. Report
  // access itself remains solely the authoritative `hasAccess` result.
  if (!access.data?.hasAccess && session.data.role !== "super_admin") {
    return (
      <AuthenticatedAnalyticsProvider user={session.data}>
        {isOpenReportPath(location.pathname) ? (
          <AuthenticatedShell
            hasReportAccess={false}
            onAccessChanged={refreshAuthorization}
            onSessionExpired={expireSession}
            role={session.data.role}
          />
        ) : <AccessPending onRefresh={retry} />}
      </AuthenticatedAnalyticsProvider>
    );
  }
  return (
    <AuthenticatedAnalyticsProvider user={session.data}>
      <AuthenticatedShell
        hasReportAccess={access.data?.hasAccess === true}
        onAccessChanged={refreshAuthorization}
        onSessionExpired={expireSession}
        role={session.data.role}
      />
    </AuthenticatedAnalyticsProvider>
  );
}

function administratorRecoveryMessage(search: string): string | undefined {
  switch (new URLSearchParams(search).get("auth_error")) {
    case "dependency_unavailable":
      return "Sign-in services are temporarily unavailable. Wait and try again.";
    case "oidc_unavailable":
      return "Google sign-in is temporarily unavailable. You can try administrator sign-in.";
    case "rate_limited":
      return "Too many sign-in attempts from your network. Wait and try again.";
    default:
      return undefined;
  }
}

function AdministratorRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const message = administratorRecoveryMessage(location.search);

  return (
    <AdministratorLogin
      message={message}
      onSignedIn={() => {
        clearProtectedQueries(queryClient);
        queryClient.removeQueries({ queryKey: authQueryKey });
        const customerTarget = consumeCustomerReturnTarget(
          browserSessionStorage(),
          window.location.origin,
        );
        navigate(customerTarget ?? "/", { replace: true });
      }}
    />
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AdministratorRoute />} path="/login/admin" />
        <Route element={<SessionGate />} path="/*" />
        <Route element={<Navigate replace to="/" />} path="*" />
      </Routes>
    </BrowserRouter>
  );
}
